/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Pure helpers shared by the Billing pages (overview, transactions, invoices,
// add funds & cards). No React here.

import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns';
import toast from 'react-hot-toast';
import type {
  BillingDetails,
  BillingProductKey,
  Invoice,
  RefundDetailedBreakdown,
  Transaction,
  TransactionGroup,
  TransactionQuery,
} from '../../types';
import { formatINR, Tone } from '../console/ui';

export type DisplayCurrency = 'INR' | 'USD';

/** Fallback used only until /settings/currency answers. */
export const DEFAULT_USD_TO_INR = 83.5;

export interface CouponValidationResult {
  valid: boolean;
  message: string;
  coupon?: {
    id: string;
    code: string;
    type: 'percentage' | 'fixed' | 'credits';
    description?: string;
    discount_value?: number;
    credit_amount?: number;
  };
  discount_amount?: number;
  credit_amount?: number;
}

/** Format an INR amount in the selected display currency (current exchange rate). */
export const money = (amountINR: number, currency: DisplayCurrency, rate: number, digits = 2) =>
  currency === 'INR' ? formatINR(amountINR, 'INR', digits) : formatINR(amountINR / (rate || DEFAULT_USD_TO_INR), 'USD', digits);

/** Signed amount: "+₹1.00" for credits, "−₹1.00" for debits. `value` is already in `currency`. */
export const formatSigned = (type: 'credit' | 'debit', value: number, currency: DisplayCurrency) => {
  const s = formatINR(Math.abs(value), currency);
  return type === 'credit' ? `+${s}` : `−${s}`;
};

/** Compact INR/USD (₹10.5K) for chart labels. */
export const moneyCompact = (amountINR: number, currency: DisplayCurrency, rate: number) => {
  const v = currency === 'INR' ? amountINR : amountINR / (rate || DEFAULT_USD_TO_INR);
  const sym = currency === 'USD' ? '$' : '₹';
  const abs = Math.abs(v);
  if (abs >= 1e7 && currency === 'INR') return `${sym}${(v / 1e7).toFixed(1)}Cr`;
  if (abs >= 1e5 && currency === 'INR') return `${sym}${(v / 1e5).toFixed(1)}L`;
  if (abs >= 1e3) return `${sym}${(v / 1e3).toFixed(1)}K`;
  return `${sym}${v.toFixed(2)}`;
};

/**
 * Transaction amount in the display currency. USD view prefers the original
 * USD amount (PayPal), then the stored exchange rate at the time of payment,
 * then the current rate.
 */
export const txAmountValue = (tx: Transaction, currency: DisplayCurrency, rate: number): number => {
  if (currency === 'INR') return tx.amount || 0;
  if (tx.original_currency === 'USD' && tx.original_amount) return tx.original_amount;
  const paypalMatch = tx.description?.match(/\$(\d+\.?\d*)\s*USD/i);
  if (paypalMatch) {
    const usd = parseFloat(paypalMatch[1]);
    if (!isNaN(usd) && usd > 0) return usd;
  }
  return (tx.amount || 0) / (tx.exchange_rate || rate || DEFAULT_USD_TO_INR);
};

export const formatTimeLeft = (hours: number): string => {
  if (hours <= 0) return 'less than 1 hour';
  if (hours < 1) return `${Math.round(hours * 60)} minutes`;
  if (hours < 24) return `${hours.toFixed(1)} hours`;
  const days = hours / 24;
  if (days < 7) return `${days.toFixed(1)} days`;
  return `${(days / 7).toFixed(1)} weeks`;
};

// ── Product classification ────────────────────────────────────────────────

export type ProductKey = 'quickpods' | 'trainpods' | 'inference' | 'storage' | 'topup' | 'other';

export const PRODUCTS: Record<ProductKey, { label: string; tone: Tone }> = {
  quickpods: { label: 'QuickPods', tone: 'warn' },
  trainpods: { label: 'TrainPods', tone: 'accent' },
  inference: { label: 'Inference', tone: 'info' },
  storage: { label: 'Object storage', tone: 'off' },
  topup: { label: 'Top-up', tone: 'ok' },
  other: { label: 'Other', tone: 'off' },
};

/** Map a wallet transaction to the product that produced it (by reference_type). */
export const productOf = (tx: Transaction): ProductKey => {
  const r = (tx.reference_type || '').toLowerCase();
  if (tx.type === 'credit' || r === 'refund') return 'topup';
  if (r.startsWith('object_storage') || r === 'bucket') return 'storage';
  if (/train|gpustack|gpu_instance/.test(r)) return 'trainpods';
  if (/infer|podvirt|token|generation|generate|serverless|llm|model/.test(r)) return 'inference';
  if (/container|nfs|vm|sandbox|notebook|gpu_execution|admin_assignment|managed_service|storage/.test(r)) return 'quickpods';
  return 'other';
};

/** Map a backend product category (wallet reports) to the UI product tag. */
export const productKeyOf = (product: BillingProductKey | string, type: string): ProductKey => {
  if (type === 'credit') return 'topup';
  switch (product) {
    case 'training':
      return 'trainpods';
    case 'inference':
    case 'agents':
      return 'inference';
    case 'object_storage':
      return 'storage';
    case 'other':
      return 'other';
    default:
      return 'quickpods'; // pods, nfs, sandboxes, notebooks, vms, managed_services
  }
};

/** Server-side filters behind each product chip on the Transactions tab. */
export const PRODUCT_FILTERS: Record<Exclude<ProductKey, 'other'>, Pick<TransactionQuery, 'type' | 'product'>> = {
  quickpods: { type: ['debit'], product: ['pods', 'nfs', 'sandboxes', 'notebooks', 'vms', 'managed_services'] },
  trainpods: { type: ['debit'], product: ['training'] },
  inference: { type: ['debit'], product: ['inference', 'agents'] },
  storage: { type: ['debit'], product: ['object_storage'] },
  topup: { type: ['credit'] },
};

/** Labels for the usage-by-product breakdown (overview + home). */
export const USAGE_LABELS: Record<BillingProductKey, string> = {
  pods: 'QuickPods · Pods',
  nfs: 'QuickPods · NFS',
  sandboxes: 'QuickPods · Sandboxes',
  managed_services: 'QuickPods · MLOps',
  notebooks: 'Notebooks',
  vms: 'Virtual machines',
  inference: 'Inference',
  agents: 'Agents',
  training: 'TrainPods',
  object_storage: 'Object storage',
  other: 'Other',
};

// ── Date ranges ──────────────────────────────────────────────────────────

export type DateRangeKey = '30d' | 'month' | 'last_month' | 'all' | 'custom';

export const DATE_RANGES: Array<{ value: DateRangeKey; label: string }> = [
  { value: '30d', label: 'Last 30 days' },
  { value: 'month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom…' },
];

const ymd = (d: Date) => format(d, 'yyyy-MM-dd');

/** Inclusive YYYY-MM-DD bounds (local calendar days) for a range. */
export const rangeBounds = (
  range: DateRangeKey,
  custom: { from: string; to: string },
  now = new Date(),
): { from?: string; to?: string } => {
  switch (range) {
    case '30d':
      return { from: ymd(subDays(now, 29)), to: ymd(now) };
    case 'month':
      return { from: ymd(startOfMonth(now)), to: ymd(now) };
    case 'last_month': {
      const prev = subMonths(now, 1);
      return { from: ymd(startOfMonth(prev)), to: ymd(endOfMonth(prev)) };
    }
    case 'custom':
      return { from: custom.from || undefined, to: custom.to || undefined };
    default:
      return {};
  }
};

/** Client-side equivalent of rangeBounds, for legacy (unfiltered) backends. */
export const inRange = (iso: string, bounds: { from?: string; to?: string }) => {
  const day = format(new Date(iso), 'yyyy-MM-dd');
  return (!bounds.from || day >= bounds.from) && (!bounds.to || day <= bounds.to);
};

/** Detail column for a server-side group, mirroring groupDetail(). */
export const serverGroupDetail = (g: TransactionGroup, product: ProductKey): string => {
  const first = new Date(g.first_at);
  const last = new Date(g.last_at);
  if (g.type === 'credit' && g.count === 1) return format(first, 'HH:mm');
  const p = parseDescription(g.description);
  const parts: string[] = [];
  if (g.count > 1) {
    parts.push(`${g.count.toLocaleString('en-IN')} ${product === 'inference' ? 'requests' : 'charges'}`);
    parts.push(`${format(first, 'HH:mm')}–${format(last, 'HH:mm')}`);
  } else {
    parts.push(format(first, 'HH:mm'));
  }
  if (p.advance || /_advance$/.test(g.reference_type)) parts.push('Advance');
  if (p.spec) parts.push(p.spec);
  return parts.join(' · ');
};

/** Resource label for a server-side group. */
export const serverGroupResource = (g: TransactionGroup) => {
  if (g.type === 'credit' && !g.reference_id) return { resource: g.description || 'Credit', mono: false };
  if (g.resource_name) {
    const mono = /^[a-z0-9][a-z0-9._-]{5,}$/i.test(g.resource_name) && /[-_.\d]/.test(g.resource_name);
    return { resource: g.resource_name, mono };
  }
  const p = parseDescription(g.description);
  return { resource: p.resource || g.reference_type || 'Charge', mono: p.mono };
};

// ── Grouping per-minute charges ──────────────────────────────────────────

export interface ChargeGroup {
  key: string;
  day: Date;
  product: ProductKey;
  resource: string;
  /** True when the resource label looks like an identifier (pod name, etc.). */
  mono: boolean;
  type: 'credit' | 'debit';
  items: Transaction[];
  /** Sum in INR. */
  amount: number;
  first: Date;
  last: Date;
  advance: boolean;
  /** Spec detail from the description, e.g. "4.0 CPU, 16.0 GB RAM, 0.05 GPU, incl. GST 18%". */
  spec?: string;
}

/** Pull "resource" + "spec" out of backend descriptions like
 *  "[Advance] Pod usage: my-pod (4.0 CPU, 16.0 GB RAM, 0.05 GPU, incl. GST 18%)". */
export const parseDescription = (description = '') => {
  let d = description.trim();
  const advance = /^\[advance\]\s*/i.test(d);
  d = d.replace(/^\[advance\]\s*/i, '').replace(/\s*\(forced\)\s*$/i, '');
  let spec: string | undefined;
  const paren = d.match(/^(.*?)\s*\((.*)\)\s*$/);
  if (paren) {
    d = paren[1];
    spec = paren[2];
  }
  const colon = d.indexOf(': ');
  const resource = colon > -1 && colon < 40 ? d.slice(colon + 2).trim() || d : d;
  const mono = /^[a-z0-9][a-z0-9._-]{5,}$/i.test(resource) && /[-_.\d]/.test(resource);
  return { resource, spec, advance, mono };
};

/**
 * Collapse debits into one row per resource per day. Credits (top-ups,
 * refunds, coupons) stay one row each.
 */
export const groupTransactions = (txs: Transaction[]): ChargeGroup[] => {
  const map = new Map<string, ChargeGroup>();
  const order: string[] = [];
  for (const tx of txs) {
    const at = new Date(tx.created_at);
    const dayKey = format(at, 'yyyy-MM-dd');
    const product = productOf(tx);
    const p = parseDescription(tx.description);
    const key =
      tx.type === 'credit'
        ? `c:${tx.id}`
        : `d:${dayKey}:${tx.reference_type || ''}:${tx.reference_id || p.resource}:${p.advance ? 'a' : ''}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        day: new Date(at.getFullYear(), at.getMonth(), at.getDate()),
        product,
        resource: tx.type === 'credit' ? tx.description : p.resource,
        mono: tx.type !== 'credit' && p.mono,
        type: tx.type,
        items: [],
        amount: 0,
        first: at,
        last: at,
        advance: p.advance,
        spec: p.spec,
      };
      map.set(key, g);
      order.push(key);
    }
    g.items.push(tx);
    g.amount += tx.amount || 0;
    if (at < g.first) g.first = at;
    if (at > g.last) g.last = at;
  }
  return order.map((k) => map.get(k)!);
};

export const groupDetail = (g: ChargeGroup): string => {
  const n = g.items.length;
  if (g.type === 'credit') return format(g.first, 'HH:mm');
  const parts: string[] = [];
  if (n > 1) {
    parts.push(`${n} ${g.product === 'inference' ? 'requests' : 'charges'}`);
    parts.push(`${format(g.first, 'HH:mm')}–${format(g.last, 'HH:mm')}`);
  } else {
    parts.push(format(g.first, 'HH:mm'));
  }
  if (g.advance) parts.push('Advance');
  if (g.spec) parts.push(g.spec);
  return parts.join(' · ');
};

// ── Downloads ────────────────────────────────────────────────────────────

const downloadBlob = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const csvCell = (s: string) => `"${(s || '').replace(/"/g, '""')}"`;

/** Raw (ungrouped) CSV export of the given transactions. */
export const downloadTransactionsCSV = (transactions: Transaction[]) => {
  if (transactions.length === 0) {
    toast.error('No transactions to download');
    return;
  }
  const headers = ['Date', 'Time', 'Description', 'Type', 'Product', 'Amount (INR)', 'Razorpay Payment ID', 'Reference ID'];
  const rows = transactions.map((tx) => {
    const date = new Date(tx.created_at);
    return [
      format(date, 'yyyy-MM-dd'),
      format(date, 'HH:mm:ss'),
      csvCell(tx.description),
      tx.type === 'credit' ? 'Credit' : 'Debit',
      PRODUCTS[productOf(tx)].label,
      tx.type === 'credit' ? (tx.amount || 0).toFixed(2) : `-${(tx.amount || 0).toFixed(2)}`,
      tx.razorpay_payment_id || '-',
      tx.reference_id || '-',
    ];
  });
  downloadBlob(
    [headers.join(','), ...rows.map((r) => r.join(','))].join('\n'),
    `podstack-transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`,
  );
  toast.success('Transactions downloaded');
};

export const downloadRefundBreakdownCSV = (details?: RefundDetailedBreakdown) => {
  if (!details?.transactions || details.transactions.length === 0) {
    toast.error('No transaction data available');
    return;
  }
  const summaryRows = [
    ['Refund Eligibility Breakdown'],
    [''],
    ['Summary'],
    ['Current Balance', `₹${details.total_balance.toFixed(2)}`],
    ['Paid via Gateway', `₹${details.refundable_before_charge.toFixed(2)}`],
    [`Gateway Charge (${details.gateway_charge_percent}%)`, `-₹${details.gateway_charge_amount.toFixed(2)}`],
    ['Net Refundable Amount', `₹${details.refundable_amount.toFixed(2)}`],
    ['Non-Refundable (Coupon Credits)', `₹${details.non_refundable_amount.toFixed(2)}`],
    [''],
    ['Transaction Details (FIFO Order)'],
    ['Date', 'Description', 'Type', 'Amount', 'Source', 'Consumed', 'Remaining', 'Refundable'],
  ];
  const txRows = details.transactions.map((tx) => [
    format(new Date(tx.date), 'yyyy-MM-dd HH:mm'),
    csvCell(tx.description),
    tx.type === 'credit' ? 'Credit' : 'Debit',
    `₹${tx.amount.toFixed(2)}`,
    tx.is_paid ? 'Payment Gateway' : 'Coupon/Promo',
    `₹${tx.consumed.toFixed(2)}`,
    `₹${tx.remaining.toFixed(2)}`,
    tx.is_paid ? `₹${tx.refundable.toFixed(2)}` : '-',
  ]);
  downloadBlob(
    [...summaryRows.map((r) => r.join(',')), ...txRows.map((r) => r.join(','))].join('\n'),
    `podstack-refund-breakdown-${format(new Date(), 'yyyy-MM-dd')}.csv`,
  );
  toast.success('Refund breakdown downloaded');
};

export const invoicePeriod = (invoice: Invoice, long = false) =>
  invoice.billing_period_start && invoice.billing_period_end
    ? `${format(new Date(invoice.billing_period_start), long ? 'MMM d, yyyy' : 'MMM d')} – ${format(
        new Date(invoice.billing_period_end),
        'MMM d, yyyy',
      )}`
    : format(new Date(invoice.created_at), 'MMMM yyyy');

const esc = (s: string | number | undefined | null) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

/** Open the invoice's PDF (if the backend stored one) or a printable copy. */
export const downloadInvoicePDF = (invoice: Invoice, billTo?: BillingDetails | null) => {
  if (invoice.invoice_pdf_url) {
    window.open(invoice.invoice_pdf_url, '_blank', 'noopener,noreferrer');
    return;
  }
  const lineItemsHtml =
    invoice.line_items
      ?.map(
        (item) => `
      <tr>
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb;">${esc(item.description)}</td>
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb;">${esc(item.resource_name || '-')}</td>
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">${(item.quantity || 0).toFixed(2)} ${esc(item.unit || 'hrs')}</td>
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">₹${(item.unit_price || 0).toFixed(4)}</td>
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb; text-align: right;">₹${(item.amount || 0).toFixed(2)}</td>
      </tr>`,
      )
      .join('') || '<tr><td colspan="5" style="padding: 12px 8px; text-align: center; color: #666;">No line items</td></tr>';

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice ${esc(invoice.invoice_number)} - Podstack</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
        .header { display: flex; justify-content: space-between; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 2px solid #F97316; }
        .logo { font-size: 28px; font-weight: bold; color: #F97316; }
        .logo-sub { font-size: 12px; color: #666; margin-top: 4px; }
        .invoice-info { text-align: right; }
        .invoice-number { font-size: 24px; font-weight: bold; color: #111; }
        .invoice-date { color: #666; margin-top: 8px; }
        .status { display: inline-block; padding: 6px 16px; border-radius: 9999px; font-size: 12px; font-weight: 600; margin-top: 12px; }
        .status-paid { background: #dcfce7; color: #166534; }
        .status-pending { background: #fef3c7; color: #92400e; }
        .status-overdue { background: #fee2e2; color: #991b1b; }
        .billing-info { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .billing-box { background: #f9fafb; padding: 20px; border-radius: 8px; flex: 1; margin-right: 20px; }
        .billing-box:last-child { margin-right: 0; }
        .billing-label { font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
        .billing-value { font-size: 14px; font-weight: 500; }
        table { width: 100%; border-collapse: collapse; margin: 30px 0; }
        th { background: #f3f4f6; padding: 14px 8px; text-align: left; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #374151; }
        th:nth-child(3), th:nth-child(4), th:nth-child(5) { text-align: right; }
        .totals { margin-top: 30px; margin-left: auto; width: 300px; }
        .total-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
        .total-row:last-child { border-bottom: none; }
        .grand-total { font-size: 18px; font-weight: bold; background: #f3f4f6; padding: 16px; border-radius: 8px; margin-top: 10px; }
        .footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #666; text-align: center; }
        .footer p { margin: 4px 0; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div class="logo">Podstack</div>
          <div class="logo-sub">Cloud Infrastructure Platform</div>
        </div>
        <div class="invoice-info">
          <div class="invoice-number">${esc(invoice.invoice_number)}</div>
          <div class="invoice-date">Issued: ${format(new Date(invoice.created_at), 'MMMM d, yyyy')}</div>
          <span class="status status-${esc(invoice.status)}">${esc(invoice.status).toUpperCase()}</span>
        </div>
      </div>
      ${
        billTo?.legal_name
          ? `<div class="billing-info"><div class="billing-box">
          <div class="billing-label">Billed To</div>
          <div class="billing-value">${esc(billTo.legal_name)}</div>
          ${billTo.gstin ? `<div style="font-size: 13px;">GSTIN: ${esc(billTo.gstin)}</div>` : ''}
          <div style="font-size: 13px; color: #555;">${esc(
            [billTo.address_line1, billTo.address_line2, billTo.city, billTo.state, billTo.postal_code, billTo.country]
              .filter(Boolean)
              .join(', '),
          )}</div>
          ${billTo.billing_email ? `<div style="font-size: 13px; color: #555;">${esc(billTo.billing_email)}</div>` : ''}
        </div></div>`
          : ''
      }
      <div class="billing-info">
        <div class="billing-box">
          <div class="billing-label">Billing Period</div>
          <div class="billing-value">${esc(invoicePeriod(invoice, true))}</div>
        </div>
        <div class="billing-box">
          <div class="billing-label">Due Date</div>
          <div class="billing-value">${format(new Date(invoice.due_date), 'MMMM d, yyyy')}</div>
        </div>
        <div class="billing-box">
          <div class="billing-label">Currency</div>
          <div class="billing-value">${esc(invoice.currency || 'INR')}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th>Resource</th>
            <th style="text-align: right;">Usage</th>
            <th style="text-align: right;">Rate</th>
            <th style="text-align: right;">Amount</th>
          </tr>
        </thead>
        <tbody>${lineItemsHtml}</tbody>
      </table>
      <div class="totals">
        <div class="total-row"><span>Subtotal</span><span>₹${(invoice.subtotal_amount || invoice.amount || 0).toFixed(2)}</span></div>
        <div class="total-row"><span>Tax (GST 18%)</span><span>₹${(invoice.tax_amount || 0).toFixed(2)}</span></div>
        <div class="total-row grand-total"><span>Total Amount</span><span>₹${(invoice.total_amount || 0).toFixed(2)}</span></div>
        ${
          invoice.paid_amount > 0
            ? `<div class="total-row" style="color: #166534;"><span>Paid</span><span>₹${invoice.paid_amount.toFixed(2)}</span></div>`
            : ''
        }
      </div>
      <div class="footer">
        <p><strong>Podstack Technologies Pvt. Ltd.</strong></p>
        <p>Thank you for using Podstack!</p>
        <p>For billing queries, contact billing@podstack.ai</p>
      </div>
    </body>
    </html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  }
};
